import { CommonModule } from '@angular/common';
import { Component, Input, OnInit, QueryList, ViewChildren } from '@angular/core';
import { ChartConfig } from '../../../core/chart/chart.component';
import { CardComponent } from '../../../core/panels/card.component';
import { DetailItemComponent } from '../../../core/panels/detail-panel/detail-item.component';
import { DetailPanelComponent } from '../../../core/panels/detail-panel/detail-panel.component';
import { SidekickChartComponent } from './sidekick-chart.component';
import { SidekickSummaryDetail } from '../../../../models/sidekick-summary.model';
import { BaseComponent } from '../../../base/base.component';

@Component({
  selector: 'app-wc-summary',
  imports: [
    CommonModule,
    DetailPanelComponent,
    DetailItemComponent,
    CardComponent,
    SidekickChartComponent,
  ],
  template: `
    <div class="tw-flex tw-gap-4">
      <div [class]="showFinancials ? 'tw-basis-2/3' : 'tw-basis-full'">
        <div class="tw-flex tw-gap-8 tw-pb-8">
          <div class="tw-basis-full">
            <app-detail-panel [dataSource]="dataSource">
              <app-detail-item
                label="Type of injury"
                name="typeOfInjury"
                labelPosition="Top"
              />
              <app-detail-item
                label="Claim status"
                name="claimStatus"
                labelPosition="Top"
              />
              <app-detail-item
                label="Summary of actions"
                name="summaryOfActions"
                labelPosition="Top"
              />
              <app-detail-item
                label="Legal aspects"
                name="legalAspects"
                labelPosition="Top"
              />
              <app-detail-item
                label="Path to resolution"
                name="pathToResolution"
                labelPosition="Top"
              />
            </app-detail-panel>
          </div>
        </div>
        <div class="tw-flex tw-gap-8 tw-pb-8">
          <div class="tw-basis-full">
            <app-card label="Claim Details">
              <div class="tw-flex tw-gap-8 tw-pb-4">
                <div class="tw-basis-1/2">
                  <app-detail-panel [dataSource]="dataSource">
                    <app-detail-item label="Claimant" name="claimant" />
                    @if (showJobTitle) {
                      <app-detail-item label="Job Title" name="jobTitle" />
                    }
                    @if (showClaimantDateOfBirth) {
                      <app-detail-item
                        label="Claimant date of birth"
                        name="claimantDateOfBirth"
                        format="d"
                      />
                    }
                    @if (showCurrentAge) {
                      <app-detail-item label="Current Age" name="currentAge" />
                    }
                    <app-detail-item
                      label="Client Location"
                      name="clientLocation"
                    />
                    <app-detail-item label="Client Name" name="clientName" />
                    @if (showAccountName) {
                      <app-detail-item
                        label="Account Name"
                        name="accountName"
                      />
                    }
                    <app-detail-item
                      label="State Of Injury"
                      name="stateOfInjury"
                    />
                    @if (showLengthOfEmployment) {
                      <app-detail-item
                        label="Length Of Employment"
                        name="lengthOfEmployment"
                      />
                    }
                    <app-detail-item
                      label="Average Weekly Wage"
                      name="averageWeeklyWage"
                      format="c"
                    />
                    <app-detail-item
                      label="Weekly Temporary Rate"
                      name="weeklyTemporaryRate"
                      format="c"
                    />
                    <app-detail-item
                      label="Percentage Of Impairment"
                      name="percentageOfImpairment"
                      format="%"
                    />
                  </app-detail-panel>
                </div>
                <div class="tw-basis-1/2">
                  <app-detail-panel [dataSource]="dataSource">
                    <app-detail-item
                      label="Date Prepared"
                      name="datePrepared"
                      format="d"
                    />
                    <app-detail-item
                      label="Date Of Loss"
                      name="dateOfLoss"
                      format="d"
                    />
                    <app-detail-item
                      label="Date Of Hire"
                      name="dateOfHire"
                      format="d"
                    />
                    <app-detail-item
                      label="Claims Examiner"
                      name="claimsExaminer"
                    />
                    <app-detail-item label="Claim Number" name="claimNumber" />
                    <app-detail-item label="Status" name="status" />
                    @if (showDateClaimReported) {
                      <app-detail-item
                        label="Date Claim Reported"
                        name="dateClaimReported"
                        format="d"
                      />
                    }
                    @if (showDateClosed) {
                      <app-detail-item
                        label="Date Of Claim Closure"
                        name="dateClosed"
                        format="d"
                      />
                    }
                    <app-detail-item
                      label="Last Day Worked"
                      name="lastDayWorked"
                      format="d"
                    />
                    <app-detail-item
                      label="Return To Work Date"
                      name="returnToWorkDate"
                      format="d"
                    />
                    <app-detail-item
                      label="Date Of MMI"
                      name="dateOfMMI"
                      format="d"
                      labelCase="none"
                    />
                  </app-detail-panel>
                </div>
              </div>
            </app-card>
          </div>
        </div>
      </div>
      @if (showFinancials) {
        <div class="tw-basis-1/3">
          <div class="tw-flex tw-pl-8 tw-flex-col tw-pb-4">
            <app-sidekick-chart
              [dataSource]="indemnityDataSource"
              title="Indemnity/Loss"
              [hasPartyDeductible]="false"
            />
            <app-sidekick-chart
              [dataSource]="medicalDataSource"
              title="Medical"
              [hasPartyDeductible]="false"
            />
            <app-sidekick-chart
              [dataSource]="expenseDataSource"
              title="Expense/Other"
              [hasPartyDeductible]="false"
            />
            <app-sidekick-chart
              [dataSource]="totalsDataSource"
              [hasPartyDeductible]="false"
            />
          </div>
        </div>
      }
    </div>
  `,
  styles: [],
})
export class WcSummaryComponent extends BaseComponent implements OnInit {
  @ViewChildren(SidekickChartComponent) private chartComponents!: QueryList<SidekickChartComponent>;

  private _dataSource: SidekickSummaryDetail | null = null;

  showFinancials = true;
  showJobTitle = true;
  showClaimantDateOfBirth = true;
  showCurrentAge = true;
  showAccountName = true;
  showLengthOfEmployment = true;
  showDateClaimReported = true;
  showDateClosed = true;

  @Input()
  set dataSource(value: SidekickSummaryDetail | null) {
    this._dataSource = value;
    if (value) {
      this.initializeCharts();
    }
  }

  get dataSource(): SidekickSummaryDetail | null {
    return this._dataSource;
  }

  expenseDataSource: any = {
    totalIncurred: 0,
    configData: new ChartConfig(),
  };
  totalsDataSource: any = {
    totalIncurred: 0,
    configData: new ChartConfig(),
  };
  medicalDataSource: any = {
    totalIncurred: 0,
    configData: new ChartConfig(),
  };
  indemnityDataSource: any = {
    totalIncurred: 0,
    configData: new ChartConfig(),
  };

  ngOnInit(): void {
    this.initFieldSecurity();
    this.showFinancials = this.session.authorize('ClaimSummaryIncurred');
    if (this._dataSource) {
      this.initializeCharts();
    }
  }

  private initFieldSecurity(): void {
    const restricted = this.session.user?.fieldSecurity?.restrictedFields || [];
    this.showJobTitle = !restricted.includes('JobTitle');
    this.showClaimantDateOfBirth = !restricted.includes('DateOfBirth');
    this.showCurrentAge = !restricted.includes('CurrentAge');
    this.showAccountName = !restricted.includes('AccountName');
    this.showLengthOfEmployment = !restricted.includes('LengthOfEmployment');
    this.showDateClaimReported = !restricted.includes('DateClaimReported');
    this.showDateClosed = !restricted.includes('DateClosed');
  }

  initializeCharts(): void {
    const paidColor = '#3b3cc2';
    const reserveColor = '#08857d';

    const financials = this.dataSource?.financials;

    const indemnityConfig = new ChartConfig();
    indemnityConfig.showLabels = false;
    indemnityConfig.height = '100px';
    indemnityConfig.addSeries(
      'Paid',
      financials?.indemnityPaid ?? 0,
      paidColor,
    );
    indemnityConfig.addSeries(
      'Reserve',
      financials?.indemnityReserve ?? 0,
      reserveColor,
    );
    this.indemnityDataSource = {
      totalIncurred: financials?.indemnityIncurred ?? 0,
      configData: indemnityConfig,
    };

    const medicalConfig = new ChartConfig();
    medicalConfig.showLabels = false;
    medicalConfig.height = '100px';
    medicalConfig.addSeries('Paid', financials?.medicalPaid ?? 0, paidColor);
    medicalConfig.addSeries(
      'Reserve',
      financials?.medicalReserve ?? 0,
      reserveColor,
    );
    this.medicalDataSource = {
      totalIncurred: financials?.medicalIncurred ?? 0,
      configData: medicalConfig,
    };

    const expenseConfig = new ChartConfig();
    expenseConfig.showLabels = false;
    expenseConfig.height = '100px';
    expenseConfig.addSeries(
      'Paid',
      financials?.expenseOtherPaid ?? 0,
      paidColor,
    );
    expenseConfig.addSeries(
      'Reserve',
      financials?.expenseOtherReserve ?? 0,
      reserveColor,
    );
    this.expenseDataSource = {
      totalIncurred: financials?.expenseOtherIncurred ?? 0,
      configData: expenseConfig,
    };

    const totalsConfig = new ChartConfig();
    totalsConfig.showLabels = false;
    totalsConfig.height = '100px';
    totalsConfig.addSeries('Paid', financials?.totalPaid ?? 0, paidColor);
    totalsConfig.addSeries(
      'Reserve',
      financials?.totalReserve ?? 0,
      reserveColor,
    );
    this.totalsDataSource = {
      totalIncurred: financials?.totalIncurred ?? 0,
      configData: totalsConfig,
    };
  }

  captureFinancialChartImages(): string[] {
    return this.chartComponents
      .map((chart) => chart.captureDonutImage())
      .filter((image): image is string => image != null && image.length > 0);
  }
}
